import React, { useState, useEffect } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  IconButton,
  Alert,
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import CloseIcon from '@mui/icons-material/Close'
import { BillingInfo } from '@/api/billingAPI'

interface BillingInformationProps {
  billingInfo?: BillingInfo | null
  onUpdate?: (updates: Partial<BillingInfo>) => void
}

// Default empty billing info when no data is provided
const EMPTY_BILLING_INFO: BillingInfo = {
  billing_email: null,
  billing_legal_name: null,
  billing_contact_name: null,
  billing_contact_phone: null,
  billing_address_line1: null,
  billing_address_line2: null,
  billing_city: null,
  billing_state: null,
  billing_postal_code: null,
  billing_country: null,
  tax_registration_number: null,
}

export default function BillingInformation({
  billingInfo,
  onUpdate,
}: BillingInformationProps) {
  const info = billingInfo ?? EMPTY_BILLING_INFO
  const [isEditing, setIsEditing] = useState(false)
  const [formData, setFormData] = useState<BillingInfo>(info)
  const [saveSuccess, setSaveSuccess] = useState(false)

  // Update form data when billingInfo prop changes
  useEffect(() => {
    setFormData(billingInfo ?? EMPTY_BILLING_INFO)
  }, [billingInfo])

  const handleChange =
    (field: keyof BillingInfo) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({ ...prev, [field]: e.target.value }))
    }

  const handleSave = () => {
    setSaveSuccess(true)
    setTimeout(() => setSaveSuccess(false), 3000)
    setIsEditing(false)
    if (onUpdate) {
      onUpdate(formData)
    }
  }

  const handleCancel = () => {
    setFormData(info)
    setIsEditing(false)
  }

  const InfoRow = ({
    label,
    value,
  }: {
    label: string
    value: string | null
  }) => (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body1">{value || '-'}</Typography>
    </Box>
  )

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Billing Information
          </Typography>

          {!isEditing ? (
            <IconButton onClick={() => setIsEditing(true)} size="small">
              <EditIcon />
            </IconButton>
          ) : (
            <IconButton onClick={handleCancel} size="small">
              <CloseIcon />
            </IconButton>
          )}
        </Box>

        {saveSuccess && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Billing information updated successfully
          </Alert>
        )}

        {!isEditing ? (
          // Display Mode
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <InfoRow label="Legal Name" value={info.billing_legal_name} />
              <InfoRow label="Billing Email" value={info.billing_email} />
              <InfoRow label="Contact Name" value={info.billing_contact_name} />
              <InfoRow label="Phone" value={info.billing_contact_phone} />
              <InfoRow
                label="Tax Registration Number"
                value={info.tax_registration_number}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <InfoRow label="Address" value={info.billing_address_line1} />
              {info.billing_address_line2 && (
                <InfoRow label="" value={info.billing_address_line2} />
              )}
              <InfoRow
                label="City"
                value={
                  info.billing_city || info.billing_postal_code
                    ? `${info.billing_city || ''}${info.billing_city && info.billing_postal_code ? ', ' : ''}${info.billing_postal_code || ''}`
                    : null
                }
              />
              <InfoRow label="Country" value={info.billing_country} />
            </Grid>
          </Grid>
        ) : (
          // Edit Mode
          <Box component="form">
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Legal Name"
                  value={formData.billing_legal_name || ''}
                  onChange={handleChange('billing_legal_name')}
                  size="small"
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Billing Email"
                  type="email"
                  value={formData.billing_email || ''}
                  onChange={handleChange('billing_email')}
                  size="small"
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Contact Name"
                  value={formData.billing_contact_name || ''}
                  onChange={handleChange('billing_contact_name')}
                  size="small"
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Phone"
                  value={formData.billing_contact_phone || ''}
                  onChange={handleChange('billing_contact_phone')}
                  size="small"
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Tax Registration Number"
                  value={formData.tax_registration_number || ''}
                  onChange={handleChange('tax_registration_number')}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Address Line 1"
                  value={formData.billing_address_line1 || ''}
                  onChange={handleChange('billing_address_line1')}
                  size="small"
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Address Line 2"
                  value={formData.billing_address_line2 || ''}
                  onChange={handleChange('billing_address_line2')}
                  size="small"
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="City"
                  value={formData.billing_city || ''}
                  onChange={handleChange('billing_city')}
                  size="small"
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Postal Code"
                  value={formData.billing_postal_code || ''}
                  onChange={handleChange('billing_postal_code')}
                  size="small"
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Country"
                  value={formData.billing_country || ''}
                  onChange={handleChange('billing_country')}
                  size="small"
                />
              </Grid>
            </Grid>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 2,
                mt: 3,
              }}
            >
              <Button
                variant="outlined"
                onClick={handleCancel}
                sx={{ textTransform: 'none' }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                sx={{
                  textTransform: 'none',
                  bgcolor: '#1C3045',
                }}
              >
                Save Changes
              </Button>
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  )
}
